import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import creatorBriefsApi from "../api/creatorBriefsApi.tsx";
import { displayAlert } from "../slice/alertSlice.tsx";
import {
  CreateCreatorBriefPayload,
  DownloadCreatorBriefsBody,
  UpdateCreatorBriefPayload,
} from "../types/creatorBriefsTypes.tsx";

interface ApiError {
  detail: string;
}

type ApiValidationError = {
  [key: string]: string[];
};

export const fetchCreatorBriefsThunk = createAsyncThunk(
  "creatorBriefs/fetchAll",
  async (_, thunkAPI) => {
    try {
      const response = await creatorBriefsApi.fetchCreatorBriefs();
      return response.data;
    } catch (e: unknown) {
      const error = e as AxiosError<ApiError>;
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || "Failed to fetch creator briefs.",
      );
    }
  },
);

export const fetchCreatorBriefByIdThunk = createAsyncThunk(
  "creatorBriefs/fetchById",
  async (id: string | number, thunkAPI) => {
    try {
      const response = await creatorBriefsApi.fetchCreatorBriefById(id);
      return response.data;
    } catch (e: unknown) {
      const error = e as AxiosError<ApiError>;
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || "Failed to fetch creator brief.",
      );
    }
  },
);

export const createCreatorBriefThunk = createAsyncThunk(
  "creatorBriefs/create",
  async (data: CreateCreatorBriefPayload, thunkAPI) => {
    try {
      const response = await creatorBriefsApi.createCreatorBrief(data);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Creator brief created successfully!",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (e instanceof AxiosError && e.response && e.response.data) {
        const errorData = e.response.data;
        if (typeof errorData === "object" && !("detail" in errorData)) {
          const validationErrors = errorData as ApiValidationError;
          Object.keys(validationErrors).forEach((key) => {
            const message = validationErrors[key].join(" ");
            thunkAPI.dispatch(
              displayAlert({
                type: "error",
                text: `${key.charAt(0).toUpperCase() + key.slice(1)}: ${message}`,
              }),
            );
          });
          return thunkAPI.rejectWithValue("Validation failed.");
        }
        const apiError = errorData as ApiError;
        const detailMessage =
          apiError.detail || "Failed to create creator brief.";
        thunkAPI.dispatch(displayAlert({ type: "error", text: detailMessage }));
        return thunkAPI.rejectWithValue(detailMessage);
      }
      const errorMessage = "An unknown error occurred.";
      thunkAPI.dispatch(displayAlert({ type: "error", text: errorMessage }));
      return thunkAPI.rejectWithValue(errorMessage);
    }
  },
);
export const updateCreatorBriefThunk = createAsyncThunk(
  "creatorBriefs/update",
  async (
    {
      id,
      data,
      callback,
    }: {
      id: string | number;
      data: UpdateCreatorBriefPayload;
      callback?: () => void;
    },
    thunkAPI,
  ) => {
    try {
      const response = await creatorBriefsApi.updateCreatorBrief(id, data);
      if (callback) {
        callback();
      }
      thunkAPI.dispatch(fetchCreatorBriefByIdThunk(id));

      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Creator brief updated successfully!",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      const error = e as AxiosError<ApiError>;
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || "Failed to update creator brief.",
      );
    }
  },
);

export const deleteCreatorBriefThunk = createAsyncThunk(
  "creatorBriefs/delete",
  async (id: string | number, thunkAPI) => {
    try {
      await creatorBriefsApi.deleteCreatorBrief(id);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Creator brief deleted successfully!",
        }),
      );
      return id; // Return the ID for removal from state
    } catch (e: unknown) {
      const error = e as AxiosError<ApiError>;
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || "Failed to delete creator brief.",
      );
    }
  },
);

export const downloadCreatorBrief = createAsyncThunk(
  "creatorBriefs/download",
  async (body: DownloadCreatorBriefsBody, thunkAPI) => {
    try {
      const response = await creatorBriefsApi.downloadCreatorBrief(body);
      if (response.status === 200) {
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download =
          response.headers["content-disposition"].match(
            /filename="(.+?)"/,
          )?.[1];
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (e: unknown) {
      const error = e as AxiosError<ApiError>;
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || "Failed to download creator brief.",
      );
    }
  },
);
